/**
 * QA Panel — missing key tracker.
 *
 * Subscribes to i18next's `missingKey` event, which fires whenever t('foo')
 * cannot resolve in the current language and falls back. Maintains a deduped,
 * counted record of every missing key per language as the user navigates.
 *
 * The bridge calls onMissingKey to broadcast incremental updates to the popup.
 */

import i18next from '../../../../i18n';
import type { MissingKeyRecord } from '../types';

type Listener = (lang: string, key: string) => void;

const records = new Map<string, MissingKeyRecord>();
const listeners = new Set<Listener>();
let installed = false;

function recordKey(lang: string, key: string): void {
    const id = `${lang}::${key}`;
    const existing = records.get(id);
    if (existing) {
        existing.count += 1;
    } else {
        records.set(id, {
            lang,
            key,
            count: 1,
            firstSeen: Date.now(),
        });
        // Only notify listeners on first occurrence to avoid spam.
        listeners.forEach((listener) => listener(lang, key));
    }
}

/**
 * Install the i18next missingKey listener. Idempotent.
 *
 * i18next only emits `missingKey` when saveMissing is enabled (or a
 * missingKeyHandler is registered), so we flip that flag here. This is a
 * runtime mutation that does nothing in production builds (the entire
 * tracker is dead-stripped via the build flag).
 */
export function installMissingKeyTracker(): void {
    if (installed) return;
    installed = true;

    i18next.options.saveMissing = true;

    i18next.on('missingKey', (lngs: string[], _ns: string, key: string) => {
        // lngs is an array because i18next supports multiple fallback langs.
        // Record under the first (primary) lang so the panel groups them sensibly.
        const primary = lngs[0] ?? i18next.language ?? 'unknown';
        recordKey(primary, key);
    });
}

/** Subscribe to first-occurrence missing key notifications. Returns unsubscribe. */
export function onMissingKey(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getMissingKeyRecords(): MissingKeyRecord[] {
    return Array.from(records.values()).sort((a, b) => b.count - a.count);
}

export function clearMissingKeys(): void {
    records.clear();
}
